import _ from "lodash";
import { useState, useMemo, useEffect } from "react";
import { ResponsiveContainer, PieChart, Pie, Cell, Sector } from "recharts";

import TimeUtils from "utils/time";
import AnalyticsService from "data/firestore/analytics";
import { MethodColors, MethodCategories } from "data/methods";

const getCategoryColor = (category) => {
  const palette =
    MethodColors[category] || MethodColors[MethodCategories.Other];
  return palette[500] || "#6c757d";
};

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
  } = props;

  const percentage = _.round((payload.value / payload.total) * 100);
  const time = TimeUtils.elapsedToString(
    TimeUtils.breakdown(payload.value, true)
  );

  AnalyticsService.throttleChartHover(
    "hover_method_breakdown",
    {},
    `${payload.name} ${time}`
  );

  // Truncate name to fit inside the donut hole
  const maxWidth = innerRadius * 1.6;
  const name = payload.name;

  return (
    <g>
      <foreignObject
        x={cx - maxWidth / 2}
        y={cy - 28}
        width={maxWidth}
        height={20}
      >
        <div
          xmlns="http://www.w3.org/1999/xhtml"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#333",
            textAlign: "center",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: "20px",
          }}
          title={name}
        >
          {name}
        </div>
      </foreignObject>
      <text x={cx} y={cy + 6} textAnchor="middle" fill="#666" fontSize={12}>
        {time}
      </text>
      <text x={cx} y={cy + 24} textAnchor="middle" fill="#999" fontSize={12}>
        {percentage}%
      </text>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 6}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={6}
      />
    </g>
  );
};

/**
 * Donut pie chart with rounded gaps and center label.
 *
 * @param {Object} props
 * @param {Array} props.categories - Category-level data: [{ name, value, total }]
 * @param {Array} props.methods - Method-level data: [{ name, value, total, category }]
 * @param {boolean} props.showBreakdown - If true, show individual methods. If false, show categories only.
 */
const StackedPieChart = ({ categories, methods, showBreakdown = false }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const data = useMemo(() => {
    if (showBreakdown) {
      return _.orderBy(methods, "value", "desc");
    }
    return _.orderBy(categories, "value", "desc");
  }, [categories, methods, showBreakdown]);

  useEffect(() => setActiveIndex(0), [showBreakdown]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <Pie
          activeIndex={activeIndex}
          activeShape={renderActiveShape}
          data={data}
          cx="50%"
          cy="50%"
          innerRadius="60%"
          outerRadius="80%"
          dataKey="value"
          paddingAngle={3}
          cornerRadius={6}
          onMouseEnter={(_, index) => setActiveIndex(index)}
          onClick={(_, index) => setActiveIndex(index)}
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={getCategoryColor(entry.category || entry.name)}
              stroke="none"
            />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
};

export default StackedPieChart;
